import type {CommentBoxConfig} from '@github-ui/comment-box'
import {IssueViewer, IssueViewerContextProvider, type OptionConfig} from '@github-ui/issue-viewer'
import {noop} from '@github-ui/noop'
import {FeatureFlagProvider} from '@github-ui/react-core/feature-flag-provider'
import {ArchiveIcon, LinkExternalIcon, ProjectSymlinkIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, Box} from '@primer/react'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useState} from 'react'

import {SidePanelUI} from '../../api/stats/contracts'
import {getEnabledFeatures} from '../../helpers/feature-flags'
import {getInitialState} from '../../helpers/initial-state'
import {getIssueItemIdentifier} from '../../helpers/issue-url'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useArchiveMemexItemsWithConfirmation} from '../../hooks/use-archive-memex-items-with-confirmation'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useRemoveMemexItemWithConfirmation} from '../../hooks/use-remove-memex-items-with-id'
import {useSidePanel} from '../../hooks/use-side-panel'
import {useSidePanelDirtyState} from '../../hooks/use-side-panel-dirty-state'
import {removeMemexItemsFromQueryClient} from '../../state-providers/memex-items/query-client-api'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'
import {IssueViewerResources} from '../../strings'
import {SidePanelToolbar} from './header'

type SharedIssueViewerProps = {owner: string; repo: string; number: number; url: string; itemId: number}

export function SharedIssueViewer(props: SharedIssueViewerProps) {
  const {loggedInUser, themePreferences, relayIds, issueViewerFeedbackLink, projectOwner} = getInitialState()
  const {
    hyperlist_issue_live_updates,
    graphql_subscriptions,
    issues_react_issue_viewer_timeline_secondary_fetch,
    issues_react_issue_viewer_prefetch_interaction_data_enabled,
  } = useEnabledFeatures()
  const memexFeatureFlags = getEnabledFeatures()

  const [currentProps, setCurrentProps] = useState<
    Omit<SharedIssueViewerProps, 'itemId'> & {itemId: number | undefined}
  >(props)

  useEffect(() => {
    setCurrentProps(props)
  }, [props])

  const {hasWritePermissions} = ViewerPrivileges()
  const {items} = useMemexItems()
  const queryClient = useQueryClient()
  const {openProjectItemInPane, reloadPaneItem, pinned} = useSidePanel()

  const onLinkClick = useCallback(
    (event: MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.shiftKey || event.button === 1 || !event.target) return
      if (!(event.target instanceof HTMLElement)) return

      const linkUrl = event.target.closest('a')?.href
      if (!linkUrl) return

      event.preventDefault()

      const itemIdentifier = getIssueItemIdentifier(linkUrl)
      const memexIssueItem =
        itemIdentifier &&
        items.find(item => {
          const comparisonIdentifier = item.getItemIdentifier()
          if (!comparisonIdentifier) return false
          return (
            comparisonIdentifier.owner === itemIdentifier.owner &&
            comparisonIdentifier.repo === itemIdentifier.repo &&
            comparisonIdentifier.number === itemIdentifier.number
          )
        })

      if (!memexIssueItem) {
        // Link is not to an issue in the project, open in new tab
        window.open(linkUrl, '_blank')
        return
      }

      openProjectItemInPane(memexIssueItem)
      setCurrentProps({
        owner: itemIdentifier.owner,
        repo: itemIdentifier.repo,
        number: itemIdentifier.number,
        url: linkUrl,
        itemId: memexIssueItem?.id,
      })
    },
    [items, openProjectItemInPane],
  )

  const {closePane} = useSidePanel()

  const commentBoxConfig: CommentBoxConfig = {
    pasteUrlsAsPlainText: loggedInUser?.paste_url_link_as_plain_text ?? false,
    useMonospaceFont: themePreferences.markdown_fixed_width_font,
  }

  const {openArchiveConfirmationDialog} = useArchiveMemexItemsWithConfirmation(undefined, undefined, undefined, () =>
    closePane({force: true}),
  )

  const {openRemoveConfirmationDialog} = useRemoveMemexItemWithConfirmation(undefined, undefined, undefined, () =>
    closePane({force: true}),
  )

  const onOpenInNewTab = useCallback(() => {
    window.open(currentProps.url)
  }, [currentProps.url])

  const onCopyLinkInProject = useCallback(() => {
    navigator.clipboard.writeText(window.location.toString())
  }, [])

  const onArchiveInProject = useCallback(() => {
    if (currentProps.itemId) {
      openArchiveConfirmationDialog([currentProps.itemId], SidePanelUI)
    }
  }, [currentProps.itemId, openArchiveConfirmationDialog])

  const onDeleteFromProject = useCallback(() => {
    if (currentProps.itemId) {
      openRemoveConfirmationDialog([currentProps.itemId], SidePanelUI)
    }
  }, [currentProps.itemId, openRemoveConfirmationDialog])

  const customEditMenuEntries = [
    <ActionList.Item key="open-in-new-tab" onSelect={onOpenInNewTab}>
      <ActionList.LeadingVisual>
        <LinkExternalIcon />
      </ActionList.LeadingVisual>
      {IssueViewerResources.openInNewTab}
    </ActionList.Item>,
    <ActionList.Item key="copy-link-in-project" onSelect={onCopyLinkInProject}>
      <ActionList.LeadingVisual>
        <ProjectSymlinkIcon />
      </ActionList.LeadingVisual>
      {IssueViewerResources.copyLinkInProject}
    </ActionList.Item>,
  ].concat(
    currentProps.itemId && hasWritePermissions
      ? [
          <ActionList.Item key="archive-in-project" onSelect={onArchiveInProject}>
            <ActionList.LeadingVisual>
              <ArchiveIcon />
            </ActionList.LeadingVisual>
            {IssueViewerResources.archiveInProject}
          </ActionList.Item>,
          <ActionList.Item variant="danger" key="delete-from-project" onSelect={onDeleteFromProject}>
            <ActionList.LeadingVisual>
              <TrashIcon />
            </ActionList.LeadingVisual>
            {IssueViewerResources.deleteFromProject}
          </ActionList.Item>,
        ]
      : [],
  )

  const onIssueUpdate = useCallback(() => {
    reloadPaneItem()
  }, [reloadPaneItem])

  const onIssueDelete = useCallback(async () => {
    if (currentProps.itemId) {
      removeMemexItemsFromQueryClient(queryClient, [currentProps.itemId])
    }
    closePane({force: true})
  }, [closePane, currentProps.itemId, queryClient])

  const [activeCommentEdits, setActiveCommentEdits] = useState<Readonly<Record<string, boolean>>>({})
  const [, setCommentsDirty] = useSidePanelDirtyState()
  const [, setBodyDirty] = useSidePanelDirtyState()

  useEffect(() => {
    // Whenever the active comment edit state changes, update the dirty state to sync
    // so that if there is any comment being edited, the state is dirty
    setCommentsDirty(Object.values(activeCommentEdits).some(s => s))
  }, [activeCommentEdits, setCommentsDirty])

  const {owner, repo, number} = currentProps

  const optionConfig: OptionConfig = {
    withLiveUpdates: hyperlist_issue_live_updates && graphql_subscriptions,
    withSecondaryFetch: issues_react_issue_viewer_timeline_secondary_fetch,
    prefetchInteractionDataEnabled: issues_react_issue_viewer_prefetch_interaction_data_enabled,
    commentBoxConfig,
    onLinkClick,
    selectedProjectId: relayIds?.memexProject,
    allowedProjectOwner: projectOwner?.login,
    customEditMenuEntries,
    issueQueriesFetchingPolicy: {
      fetchPolicy: 'store-and-network',
    },
    onIssueUpdate,
    timelineEventBaseUrl: `/${owner}/${repo}/issues`,
    onIssueDelete,
    onClose: () => closePane({force: true}),
    feedbackLink: issueViewerFeedbackLink,
    showRepositoryPill: true,
    backButtonText: 'Back to project',
    navigateBack: () => closePane({force: true}),
    navigate: noop,
    titleAs: 'h2',
    innerSx: {
      pl: 3,
      pr: 3,
    },
    additionalHeaderActions: <SidePanelToolbar showCloseButton={false} />,
    onCommentEditStart(commentId) {
      setActiveCommentEdits(previousCommentEditStates => {
        if (previousCommentEditStates[commentId]) return previousCommentEditStates
        return {
          ...previousCommentEditStates,
          [commentId]: true,
        }
      })
    },
    onCommentEditCancel(commentId) {
      setActiveCommentEdits(previousCommentEditStates => {
        if (!previousCommentEditStates[commentId]) return previousCommentEditStates
        return {
          ...previousCommentEditStates,
          [commentId]: false,
        }
      })
    },
    onIssueEditStateChange: setBodyDirty,
  }

  return (
    <Box sx={{position: 'relative', height: '100%', overflowY: pinned ? undefined : 'auto'}}>
      <Box sx={{m: 0, height: '100%'}}>
        <IssueViewerContextProvider>
          <FeatureFlagProvider features={memexFeatureFlags}>
            <IssueViewer
              itemIdentifier={{
                owner,
                repo,
                number,
                type: 'Issue',
              }}
              optionConfig={optionConfig}
            />
          </FeatureFlagProvider>
        </IssueViewerContextProvider>
      </Box>
    </Box>
  )
}

try{ SharedIssueViewer.displayName ||= 'SharedIssueViewer' } catch {}